// The dispatch board's lanes as last seen (names, initials, colours, time zone), so the next visit
// draws them before the board arrives (L-lanes, docs/design/states-pass.md). Staff names must not
// outlive a session on a shared office computer: signing out forgets them.

import type { Board, BoardTech } from './board-model.ts';

type Lane = Pick<BoardTech, 'id' | 'name' | 'initials' | 'color'>;
const PREFIX = 'flow.board.lanes:';
const lanesKey = (tenant: string) => PREFIX + tenant;

export function cachedLanes(tenant: string): { lanes: Lane[]; timeZone?: string } {
  try {
    const v = JSON.parse(localStorage.getItem(lanesKey(tenant)) ?? 'null') as {
      lanes?: Lane[];
      timeZone?: string;
    } | null;
    return {
      lanes: Array.isArray(v?.lanes) ? v.lanes : [],
      ...(v?.timeZone ? { timeZone: v.timeZone } : {}),
    };
  } catch {
    return { lanes: [] };
  }
}
export function keepLanes(tenant: string, b: Board): void {
  try {
    const lanes = b.technicians.map(({ id, name, initials, color }) => ({ id, name, initials, color }));
    localStorage.setItem(lanesKey(tenant), JSON.stringify({ lanes, timeZone: b.timeZone }));
  } catch {
    // storage blocked: the lanes simply wait for the board
  }
}

/** Forgets every company's cached lanes (on sign-out). */
export function forgetLanes(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    // storage blocked: nothing was kept
  }
}

/**
 * The board's density as each person last chose it on this browser (BL-100): Comfortable unless they
 * picked Compact. Keyed by their membership, so on a shared office computer each keeps their own; it
 * names nobody, so signing out keeps it.
 */
export type Density = 'comfortable' | 'compact';
const densityKey = (tenant: string, who: string | undefined) => `flow.board.density:${tenant}:${who ?? ''}`;

export function boardDensity(tenant: string, who: string | undefined): Density {
  try {
    return localStorage.getItem(densityKey(tenant, who)) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}
export function keepDensity(tenant: string, who: string | undefined, d: Density): void {
  try {
    localStorage.setItem(densityKey(tenant, who), d);
  } catch {
    // storage blocked: the choice lasts until the page is left
  }
}

/**
 * Whether a person groups the board's lanes by division (the corner's group button, 25 Oct), as they
 * last left it on this browser: grouped until they turn it off, kept like the density.
 */
export function readGrouped(tenant: string, who: string | undefined): boolean {
  try {
    return localStorage.getItem(`${densityKey(tenant, who)}:grouped`) !== 'no';
  } catch {
    return true;
  }
}
export function keepGrouped(tenant: string, who: string | undefined, grouped: boolean): void {
  try {
    localStorage.setItem(`${densityKey(tenant, who)}:grouped`, grouped ? 'yes' : 'no');
  } catch {
    // storage blocked: the choice lasts until the page is left
  }
}

/**
 * Concept A (26 Oct): a person's own grouping on this browser, only where it differs from the
 * company's way (dispatch.openGrouped); null when they follow it.
 */
export function readGroupedChoice(tenant: string, who: string | undefined): boolean | null {
  try {
    const v = localStorage.getItem(`${densityKey(tenant, who)}:grouped`);
    return v === 'yes' ? true : v === 'no' ? false : null;
  } catch {
    return null;
  }
}
export function keepGroupedChoice(tenant: string, who: string | undefined, grouped: boolean | null): void {
  try {
    const key = `${densityKey(tenant, who)}:grouped`;
    if (grouped === null) localStorage.removeItem(key);
    else localStorage.setItem(key, grouped ? 'yes' : 'no');
  } catch {
    // storage blocked: the choice lasts until the page is left
  }
}

/**
 * Which of the board's trays a person has folded (BL-100), as they last left them on this browser:
 * Needs a look, Waiting and Paused, each open until folded. Kept like the density, per company and
 * membership, through sign-out.
 */
export type Tray = 'looks' | 'waiting' | 'paused';
export type TrayFolds = Partial<Record<Tray, boolean>>;
const foldsKey = (tenant: string, who: string | undefined) => `flow.board.folds:${tenant}:${who ?? ''}`;

export function trayFolds(tenant: string, who: string | undefined): TrayFolds {
  try {
    const v = JSON.parse(localStorage.getItem(foldsKey(tenant, who)) ?? 'null') as unknown;
    if (!v || typeof v !== 'object') return {};
    const out: TrayFolds = {};
    for (const t of ['looks', 'waiting', 'paused'] as const)
      if ((v as Record<string, unknown>)[t] === true) out[t] = true;
    return out;
  } catch {
    return {};
  }
}
export function keepTrayFolds(tenant: string, who: string | undefined, folds: TrayFolds): void {
  try {
    localStorage.setItem(foldsKey(tenant, who), JSON.stringify(folds));
  } catch {
    // storage blocked: the trays fold until the page is left
  }
}
